import { useCallback, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ASSISTANT_WINDOW_LAYER, isPageModalOpen } from '@/features/ai-chat/pageModal'
import { cn } from '@/lib/utils'
import { useAssistantWindowInternal } from './assistantWindowContext'
import { useIsolateWhilePageModal } from './useIsolateWhilePageModal'

/**
 * 顶栏 AI 入口（§1 第 1 点，替换原右下角悬浮球）：closed → 打开通用助手；open → 最小化；minimized → 恢复。
 * 有新回复 / 有卡待确认时带小圆点。
 *
 * **抬到页面 flyout 之上**（Frank 09-14 定）：页面右侧 flyout（z-50，从 top:0 起）会盖住顶栏右半边，
 * 浮窗关掉后就点不到入口。顶栏外层是 z-10 的层叠上下文，按钮自己抬 z 出不去（整条顶栏也不能抬，会盖住
 * flyout 的标题栏和关闭按钮）——所以顶栏里只留同尺寸占位，真正的按钮 portal 到 body，按**文档坐标** absolute 对齐占位，
 * 放在浮窗那一层。不用 fixed（Plane #49，09-17 修）：Dashboard 这类整页滚动的页面上，fixed 的入口不随顶栏滚走、浮在内容上，
 * 点它开浮窗触发重测又被放到视口外，滚回顶部没有重测 → 顶栏只剩空占位再也点不到。absolute 让整页滚动由浏览器带着走（不抖），
 * 祖先容器滚动则靠 scroll 监听重测。代价：按钮在 DOM 末尾，键盘 Tab 顺序排在页面之后。
 * 抬上来会压住 flyout 标题栏里的按钮（任务详情的 Add reminder / Watch），所以有面板挡着时让到面板左边缘外侧（Frank 09-14 选 A）。
 *
 * **页面模态对话框开着时沉到遮罩下面**（09-17 走查）：z-[90] 的入口浮在弹窗遮罩之上不变暗，看着像能点的顶层控件。
 * 模态期间层级降到遮罩之下，跟页面其余部分一起被遮住；让位判定穿过遮罩看下面的面板（见 coveringPanelLeft），位置不跳。
 * 浮窗 / 最小化小条仍留在遮罩之上（只读，useIsolateWhilePageModal）——那是正在看的内容；入口只是开关，模态期间不需要。
 *
 * ⚠️ 刻意**没有** `hasPermission('ai:chat:execute')` 门：它是 base 地板码，后端对所有人隐式授予，
 * 而 /system/user/current/info 的 permissions 不含任何地板码——加门会把所有非超管挡在外面（cf0b6a054 犯过）。
 * 真被收回权限时后端会在流里给 403（useChatStream 已处理成 errorNoPermission 文案）。
 */
/** 让位时与 flyout 左边缘的间距 */
const STEP_ASIDE_GAP = 8
/** 页面模态开着时入口的层：低于对话框遮罩（z-50 起），被遮罩盖住 */
const UNDER_PAGE_MODAL_LAYER = 'z-40'
const OWN_SURFACES = '[data-assistant-entry], [data-assistant-window], [data-assistant-minimized-bar]'

/**
 * 占位是否被页面上的浮层面板（右侧 flyout / Sheet）盖住；盖住就返回面板左边缘，否则 null。
 * 在占位的左端 / 中心 / 右端做命中检测（只看中心会漏掉：live 里中心正好落在两个图标的缝里），
 * 每个点从上往下看命中栈里的非助手元素：先碰到顶栏自己 = 没被盖；否则往上找到「不从视口左边缘起」的最外层祖先当面板。
 * 从左边缘起的是遮罩 / fixed inset-0 外壳：它本身不算面板，但**要接着往下看**——09-17 走查：flyout 开着再开结单弹窗，
 * 最上层是弹窗遮罩，只看最上层就判成「没被面板盖住」，入口跳回原位压住 flyout 标题栏的 Watch。
 */
function coveringPanelLeft(anchor: HTMLElement, r: DOMRect): number | null {
  if (typeof document.elementsFromPoint !== 'function') return null
  const topBar = anchor.closest('header') ?? anchor.parentElement
  const y = r.top + r.height / 2
  let left: number | null = null
  for (const x of [r.left + 2, r.left + r.width / 2, r.right - 2]) {
    let panel: Element | null = null
    for (const hit of document.elementsFromPoint(x, y)) {
      if (hit.closest(OWN_SURFACES)) continue
      if (topBar?.contains(hit)) break
      for (let cur: Element | null = hit; cur && cur !== document.body; cur = cur.parentElement) {
        // 从视口左边缘起的层 = 全屏外壳 / 遮罩，停。不按「宽度满不满屏」判：live 09-14 实测 fixed inset-0 外壳比
        // innerWidth / html.clientWidth 窄 8px，会被当成面板，入口跑到 left:-44
        if (cur.getBoundingClientRect().left <= 0) break
        panel = cur
      }
      if (panel) break
    }
    if (!panel) continue
    const pl = panel.getBoundingClientRect().left
    left = left === null ? pl : Math.min(left, pl)
  }
  return left
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * 从占位的位置按文档顺序往前 / 往后，把焦点交给第一个**真的聚焦成功**的元素：入口在 body 末尾，Tab 要按占位的位置走。
 * 逐个试而不是只挑一个：display:none / visibility:hidden 的元素 focus() 静默失败，只挑一个会把键盘困在入口上（review 09-14）。
 * 返回是否交出去了；一个都不行就不拦 Tab，交给浏览器。
 */
function focusNeighbourOf(anchor: HTMLElement, forward: boolean): boolean {
  const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter(el => el !== anchor && el.tabIndex >= 0 && !el.closest('[data-assistant-entry], [hidden], [inert]'))
  const bit = forward ? Node.DOCUMENT_POSITION_FOLLOWING : Node.DOCUMENT_POSITION_PRECEDING
  const candidates = all.filter(el => anchor.compareDocumentPosition(el) & bit)
  if (!forward) candidates.reverse()
  for (const el of candidates) {
    el.focus()
    if (document.activeElement === el) return true
  }
  return false
}

type AssistantWindow = ReturnType<typeof useAssistantWindowInternal>

/** closed → 打开通用助手；open → 最小化；minimized → 恢复。顶栏入口与手机「更多」里的入口共用。 */
function toggleAssistant(
  windowState: AssistantWindow['state']['windowState'],
  { openAssistant, minimize, restore }: Pick<AssistantWindow, 'openAssistant' | 'minimize' | 'restore'>,
) {
  if (windowState === 'open') minimize()
  else if (windowState === 'minimized') restore()
  else openAssistant({ entry: 'global' })
}

function hasAttention(attention: AssistantWindow['attention']): boolean {
  return attention === 'awaitingConfirmation' || attention === 'newReply' || attention === 'replyFailed'
}

/** 助手有新回复 / 待确认 / 回复失败。手机上入口收进「更多」，「⋯」上的红点要算上它。 */
export function useAssistantAttention(): boolean {
  return hasAttention(useAssistantWindowInternal().attention)
}

/**
 * 手机「更多」面板里的 AI 入口：普通按钮，不走顶栏入口的 portal + 让位那一套。
 *
 * 为什么不直接把 AssistantTopBarButton 放进去：它在原地只留占位，真按钮 portal 到 body。
 * 放进 Popover 后，按下真按钮在 Radix 看来是「点到面板外面」，pointerdown 时面板就关了，
 * 占位卸载、真按钮跟着卸载，click 落空。手机上 flyout 占满全宽，也没有地方让位。
 */
export function AssistantMenuButton({ onActivate }: { onActivate?: () => void }) {
  const { t } = useTranslation('chrome')
  const { state, openAssistant, minimize, restore, attention } = useAssistantWindowInternal()
  const dot = hasAttention(attention)
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        toggleAssistant(state.windowState, { openAssistant, minimize, restore })
        onActivate?.()
      }}
      aria-label={t('aiChat.openAssistant')}
      aria-expanded={state.windowState === 'open'}
      title={t('aiChat.title')}
      data-testid="assistant-menu-button"
      className="relative h-9 w-9 text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      <Sparkles className="h-4 w-4" />
      {dot && <span data-testid="assistant-attention-dot" className={cn('absolute right-1.5 top-1.5 h-2 w-2 rounded-full', attention === 'replyFailed' ? 'bg-destructive' : 'bg-primary')} />}
    </Button>
  )
}

export function AssistantTopBarButton() {
  const { t } = useTranslation('chrome')
  const { state, openAssistant, minimize, restore, attention, triggerRef } = useAssistantWindowInternal()
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null)
  const [host, setHost] = useState<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const [underModal, setUnderModal] = useState(false)

  // 页面模态对话框开着时点入口不能把对话框关掉（与浮窗 / 小条同一套隔离）
  // 助手关闭时浮窗元素不存在：按入口自己判（review 09-14：否则关闭态点入口会把页面对话框关掉）
  const windowEl = useCallback(() => document.querySelector<HTMLElement>('[data-assistant-window]') ?? host, [host])
  useIsolateWhilePageModal(host, windowEl)

  const measure = useCallback(() => {
    if (!anchor) return
    setUnderModal(isPageModalOpen(windowEl()))
    const r = anchor.getBoundingClientRect()
    // 还没布局出来（0 尺寸）就先不显示，避免在左上角闪一下
    if (!r.width && !r.height) { setPos(null); return }
    const panelLeft = coveringPanelLeft(anchor, r)
    const aside = panelLeft === null ? null : panelLeft - r.width - STEP_ASIDE_GAP
    // 让位会跑出屏幕（窄屏上面板 max-w 92vw）就留在原位：被盖住仍可点，出屏就彻底点不到了
    // 视口坐标 → 文档坐标（宿主 absolute 挂在 body 下）；让位判定仍按视口算，再一起换算
    const next = { left: (aside === null || aside < 0 ? r.left : aside) + window.scrollX, top: r.top + window.scrollY }
    setPos(prev => (prev && prev.left === next.left && prev.top === next.top ? prev : next))
  }, [anchor, windowEl])

  useLayoutEffect(() => {
    if (!anchor) return
    measure()
    let raf = 0
    // 页面 flyout 开合靠改 class / 挂 portal + 过渡动画，不触发 resize：观察 DOM 变化与过渡结束，按帧节流重算
    const schedule = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; measure() }) }
    window.addEventListener('resize', measure)
    // 整页滚动 absolute 自己跟着走；这里兜底祖先容器滚动（顶栏被滚动容器带着挪）与滚动后页面面板的遮挡变化
    document.addEventListener('scroll', schedule, { capture: true, passive: true })
    // 自写 Flyout 滑入是 transition，Radix Sheet 是 CSS animation：两种结束都要重算
    document.addEventListener('transitionend', schedule, true)
    document.addEventListener('animationend', schedule, true)
    // 侧栏折叠 / 顶栏里其它按钮宽度变化会挪动占位：观察顶栏本身
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(anchor.closest('header') ?? anchor.parentElement ?? anchor)
    // 只在助手自己的浮层里变的 DOM（流式输出、入口自己的 style）不影响页面面板，不重算
    const mo = new MutationObserver(records => {
      if (records.every(m => (m.target instanceof Element ? m.target : m.target.parentElement)?.closest(OWN_SURFACES))) return
      schedule()
    })
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'data-state'] })
    return () => {
      window.removeEventListener('resize', measure)
      document.removeEventListener('scroll', schedule, { capture: true })
      document.removeEventListener('transitionend', schedule, true)
      document.removeEventListener('animationend', schedule, true)
      ro?.disconnect(); mo.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [anchor, measure])

  const onClick = () => toggleAssistant(state.windowState, { openAssistant, minimize, restore })
  const dot = hasAttention(attention)

  return (
    <>
      {/* 键盘 Tab 顺序留在顶栏（§6 第 11 点）：占位接住焦点转给入口；入口自己不进自然 Tab 顺序 */}
      <span
        ref={setAnchor}
        tabIndex={0}
        // 可聚焦就得有名字：读屏在浏览模式下会读到它（聚焦时立刻转给入口）
        aria-label={t('aiChat.openAssistant')}
        data-assistant-entry-anchor=""
        className="inline-block h-9 w-9 flex-none outline-none"
        onFocus={() => triggerRef.current?.focus()}
      />
      {createPortal(
        <div
          ref={setHost}
          data-assistant-entry=""
          className={cn('pointer-events-auto absolute', underModal ? UNDER_PAGE_MODAL_LAYER : ASSISTANT_WINDOW_LAYER)}
          style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
        >
          <Button
            ref={el => { triggerRef.current = el }}
            variant="ghost"
            size="icon"
            onClick={onClick}
            tabIndex={-1}
            onKeyDown={e => {
              if (e.key !== 'Tab' || !anchor) return
              if (focusNeighbourOf(anchor, !e.shiftKey)) e.preventDefault()
            }}
            aria-label={t('aiChat.openAssistant')}
            aria-expanded={state.windowState === 'open'}
            title={t('aiChat.title')}
            className="relative h-9 w-9 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Sparkles className="h-4 w-4" />
            {dot && <span data-testid="assistant-attention-dot" className={cn('absolute right-1.5 top-1.5 h-2 w-2 rounded-full', attention === 'replyFailed' ? 'bg-destructive' : 'bg-primary')} />}
          </Button>
        </div>,
        document.body,
      )}
    </>
  )
}
